import React, { useCallback } from 'react';
import { Controller, useFormContext, ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Stack,
  Autocomplete,
  Chip,
  Grid,
  Typography,
  Avatar,
  Paper,
  Button,
  TextFieldProps,
  AutocompleteProps,
  MenuItem,
} from '@mui/material';

import { SettingsFields } from '@bandido/shared/types';

import { useGetAllUsers } from '@frontend/features/users/queries';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { useEditor } from '@frontend/features/cases/hooks/useEditor';
import { ControlledSelect } from '@frontend/features/common/components/ControlledSelect';
import { useGetSettingsByAgencyId } from '@frontend/features/agencies/queries';

import { AutocompleteMail } from '../../types';
import { isValidEmail } from '@frontend/features/common/helper';
import { FormData } from '../../types';
import { InputLabel } from '../InputLabel';

type EmailFieldName = 'to' | 'cc' | 'bcc';

interface GetTagProps {
  key: number;
}

interface EmailFormProps {
  agencyId?: number;
}

export const EmailForm = ({ agencyId }: EmailFormProps) => {
  const { t } = useTranslation();
  const { showCc, showBcc, onToggleCc, onToggleBcc } = useEditor();
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<FormData>();
  const [inputValue, setInputValue] = useDebouncedState('', 500);

  const { data: users } = useGetAllUsers({
    search: inputValue,
    pageSize: 10,
  });

  const { data: settings } = useGetSettingsByAgencyId({
    id: agencyId,
    params: { name: SettingsFields.SENDGRID_INBOUND_MAILS },
  });

  const inboundMailsSetting: string[] = React.useMemo(() => {
    try {
      return JSON.parse(settings?.[SettingsFields.SENDGRID_INBOUND_MAILS] ?? '[]');
    } catch (_error) {
      return [];
    }
  }, [settings]);

  React.useEffect(() => {
    const [initialFrom] = inboundMailsSetting;

    if (!getValues('from')) setValue('from', initialFrom);
  }, [getValues, inboundMailsSetting, setValue]);

  const handleInputChange = useCallback((value: string) => setInputValue(value), [setInputValue]);

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: AutocompleteMail) => (
      <li {...props} key={option.id}>
        <Grid container alignItems="center">
          <Grid item sx={{ display: 'flex', width: 44 }}>
            <Avatar sx={{ width: 32, height: 32 }} />
          </Grid>
          <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
            <Typography variant="body2" color="text.secondary">
              {option.email}
            </Typography>
          </Grid>
        </Grid>
      </li>
    ),
    [],
  );

  const renderTags = useCallback(
    (chip: AutocompleteMail[], getTagProps: (params: { index: number }) => GetTagProps) =>
      chip?.map((option, index: number) => {
        const { key, ...tagProps } = getTagProps({ index });
        const fullName = `${option.firstName ?? ''} ${option.lastName ?? ''}`;
        const label = fullName.trim() ? fullName : option.email;
        return <Chip size="small" label={label} key={key} {...tagProps} />;
      }),
    [],
  );

  const createAutocompleteProps = useCallback(
    (fieldName: EmailFieldName): AutocompleteProps<AutocompleteMail, true, true, true> => ({
      fullWidth: true,
      multiple: true,
      disableClearable: true,
      clearOnBlur: true,
      freeSolo: true,
      size: 'small',
      sx: {
        '& .MuiFormControl-root': {
          margin: '0 !important',
        },
        '& .MuiInputBase-input': {
          fontSize: '16px',
        },
      },
      getOptionLabel: (option: string | AutocompleteMail) => {
        if (typeof option === 'string') {
          return option;
        }
        return option.email;
      },
      isOptionEqualToValue: (option: AutocompleteMail, value: AutocompleteMail) => option.email === value.email,
      filterOptions: (x: AutocompleteMail[]) => x,
      options: users?.items || [],
      autoComplete: true,
      includeInputInList: true,
      filterSelectedOptions: true,
      noOptionsText: t('noteEditor.noOptions'),
      onInputChange: (_: React.SyntheticEvent, newInputValue: string) => {
        handleInputChange(newInputValue);
      },
      renderTags,
      renderOption,
      renderInput: (params) => (
        <TextField
          {...(params as TextFieldProps)}
          variant="standard"
          margin="normal"
          InputProps={{ ...params.InputProps, disableUnderline: true }}
          size="small"
          error={!!errors[fieldName]}
          sx={{
            '& .MuiInputBase-root': {
              paddingRight: fieldName === 'to' ? '120px !important' : undefined,
            },
          }}
        />
      ),
    }),
    [users, t, handleInputChange, renderTags, renderOption, errors],
  );

  const handleEmailChange = useCallback(
    (
      field: ControllerRenderProps<FormData, EmailFieldName>,
      newValue: string | AutocompleteMail | Array<string | AutocompleteMail> | null,
    ) => {
      if (newValue === null) {
        field.onChange([]);
      } else if (Array.isArray(newValue)) {
        const updatedMails = newValue.map((item) => {
          if (typeof item === 'string') {
            return isValidEmail(item) ? { id: item, email: item } : null;
          }
          return isValidEmail(item.email) ? item : null;
        });
        const newEmails = updatedMails.filter((mail): mail is AutocompleteMail => mail !== null);
        field.onChange(newEmails);
      } else if (typeof newValue === 'string') {
        if (isValidEmail(newValue)) {
          field.onChange([{ id: newValue, email: newValue }]);
        }
      } else {
        field.onChange([newValue]);
      }
    },
    [],
  );

  const handleEmailBlur = useCallback((field: ControllerRenderProps<FormData, EmailFieldName>, newMail: string) => {
    if (newMail) {
      handleEmailChange(field, [...field.value, { id: newMail, email: newMail, firstName: '', lastName: '' }]);
    }
  }, []);

  return (
    <Stack>
      <Box>
        <InputLabel text={t('noteEditor.from')}>
          <ControlledSelect
            sx={{
              '& .MuiInputBase-root': {
                margin: 0,
                '> div': {
                  padding: 0,
                },
              },
            }}
            variant="standard"
            name="from"
            required
            disableUnderline
            control={control}
            size="small"
            fullWidth
          >
            {inboundMailsSetting.map((managedMail) => (
              <MenuItem key={managedMail} value={managedMail}>
                {managedMail}
              </MenuItem>
            ))}
          </ControlledSelect>
        </InputLabel>
      </Box>
      <Paper
        variant="outlined"
        sx={{
          border: 'none',
          position: 'relative',
          backgroundColor: 'transparent',
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <Controller
            name="to"
            control={control}
            rules={{
              validate: (value) => value.length > 0 || t('noteEditor.errors.email'),
            }}
            render={({ field }) => (
              <InputLabel error={!!errors.to} text={t('noteEditor.to')}>
                <Autocomplete
                  {...createAutocompleteProps('to')}
                  {...field}
                  onChange={(_, newValue) => handleEmailChange(field, newValue)}
                  onBlur={(e) => handleEmailBlur(field, (e.target as HTMLInputElement).value)}
                />
              </InputLabel>
            )}
          />
          <Box
            sx={{
              position: 'absolute',
              right: 8,
              bottom: 0,
              display: 'flex',
              gap: 1,
              padding: (theme) => theme.spacing(0.5, 1),
              borderRadius: 1,
              zIndex: 1,
              '& .MuiButton-root': {
                color: 'text.main',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'primary.main',
                },
              },
            }}
          >
            <Button size="small" onClick={onToggleCc} sx={{ minWidth: 'auto', textTransform: 'none' }}>
              Cc
            </Button>
            <Button size="small" onClick={onToggleBcc} sx={{ minWidth: 'auto', textTransform: 'none' }}>
              Bcc
            </Button>
          </Box>
        </Box>
      </Paper>
      {showCc && (
        <Controller
          name="cc"
          control={control}
          render={({ field }) => (
            <InputLabel text={t('noteEditor.cc')}>
              <Autocomplete
                {...createAutocompleteProps('cc')}
                {...field}
                onChange={(_, newValue) => handleEmailChange(field, newValue)}
                onBlur={(e) => handleEmailBlur(field, (e.target as HTMLInputElement).value)}
              />
            </InputLabel>
          )}
        />
      )}
      {showBcc && (
        <Controller
          name="bcc"
          control={control}
          render={({ field }) => (
            <InputLabel text={t('noteEditor.bcc')}>
              <Autocomplete
                {...createAutocompleteProps('bcc')}
                {...field}
                onChange={(_, newValue) => handleEmailChange(field, newValue)}
                onBlur={(e) => handleEmailBlur(field, (e.target as HTMLInputElement).value)}
              />
            </InputLabel>
          )}
        />
      )}
      <Controller
        name="subject"
        control={control}
        rules={{
          required: t('noteEditor.errors.subject'),
        }}
        render={({ field }) => (
          <InputLabel error={!!errors.subject} text={t('noteEditor.subject')}>
            <TextField
              {...field}
              fullWidth
              InputProps={{ disableUnderline: true, error: !!errors.subject }}
              sx={{
                margin: 0,
                input: {
                  padding: 0,
                  fontSize: '16px',
                },
              }}
              variant="standard"
              size="small"
              margin="normal"
              error={!!errors.subject}
            />
          </InputLabel>
        )}
      />
    </Stack>
  );
};

EmailForm.displayName = 'EmailForm';
